import { useEffect, useMemo, useState } from "react";
import StatusOverviewCard from "../../components/stats/StatusOverviewCard";
import { fetchProjectStats, fetchWorkorderStats } from "../../services/statsService";

const WORKORDER_STATUS_COLORS = Object.freeze({
  concept: "bg-slate-400",
  gereed_voor_planning: "bg-amber-400",
  gepland: "bg-sky-500",
  in_uitvoering: "bg-indigo-500",
  afgerond: "bg-emerald-500",
  gesloten: "bg-teal-600",
  wacht_op_materiaal: "bg-orange-500",
  wacht_op_klant: "bg-yellow-500",
  verlopen: "bg-rose-500",
});

const PROJECT_STATUS_COLORS = Object.freeze({
  concept: "bg-slate-400",
  voorbereiding: "bg-cyan-500",
  actief: "bg-blue-500",
  in_uitvoering: "bg-indigo-500",
  on_hold: "bg-amber-500",
  financiele_controle: "bg-fuchsia-500",
  afgerond: "bg-emerald-500",
});

function mapStatusesForCard(statuses = [], colorMap = {}) {
  return (Array.isArray(statuses) ? statuses : []).map((status) => ({
    key: status.key,
    label: status.label,
    value: Number(status.count || 0),
    count: Number(status.count || 0),
    colorClass: colorMap[status.key] || "bg-slate-300",
  }));
}

export default function StatusOverviewDemoPage() {
  const [activeWorkorderStatus, setActiveWorkorderStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [workorderStats, setWorkorderStats] = useState({ total: 0, statuses: [] });
  const [projectStats, setProjectStats] = useState({ total: 0, statuses: [] });

  useEffect(() => {
    let active = true;

    async function loadStats() {
      try {
        setLoading(true);
        setError("");
        const [workorders, projects] = await Promise.all([
          fetchWorkorderStats({ period: "month" }),
          fetchProjectStats({ period: "month" }),
        ]);
        if (!active) return;
        setWorkorderStats(workorders || { total: 0, statuses: [] });
        setProjectStats(projects || { total: 0, statuses: [] });
      } catch {
        if (!active) return;
        setError("Statistieken laden mislukt.");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadStats();
    return () => {
      active = false;
    };
  }, []);

  const workorderCardStatuses = useMemo(
    () => mapStatusesForCard(workorderStats?.statuses, WORKORDER_STATUS_COLORS),
    [workorderStats]
  );
  const projectCardStatuses = useMemo(
    () => mapStatusesForCard(projectStats?.statuses, PROJECT_STATUS_COLORS),
    [projectStats]
  );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Status Overview</h1>
        <p className="text-sm text-slate-500">
          Realtime statusverdeling voor de huidige tenant.
        </p>
      </div>

      {error ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      <StatusOverviewCard
        title="Werkbonnen deze maand"
        subtitle="In huidige selectie"
        statuses={workorderCardStatuses}
        totalOverride={Number(workorderStats?.total || 0)}
        totalSuffix=" werkbonnen"
        onStatusClick={(statusKey) => setActiveWorkorderStatus(statusKey)}
        emptyLabel={loading ? "Werkbon stats laden..." : "Geen werkbondata beschikbaar."}
      />

      {activeWorkorderStatus ? (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          Geselecteerde status: <span className="font-medium">{activeWorkorderStatus}</span>
        </p>
      ) : null}

      <StatusOverviewCard
        title="Projecten deze maand"
        subtitle="In huidige selectie"
        statuses={projectCardStatuses}
        totalOverride={Number(projectStats?.total || 0)}
        totalSuffix=" projecten"
        emptyLabel={loading ? "Project stats laden..." : "Geen projectdata beschikbaar."}
      />
    </div>
  );
}
